import type { Page } from '@namu/protocol'
import { ApiError, path } from './client'
import type { ApiClient, Query } from './client'
import type {
  CalendarDate,
  GapCommunity,
  GapStatus,
  Instant,
  KnowledgeGap,
  Locale,
  LoopState,
  RumorSignal,
  RumorVerdict,
  TopicSignal,
} from './types'

/**
 * The part no other tool can do, because no other tool is listening.
 *
 * Everything here is computed from conversations and everything here is
 * aggregate. None of it needs `share_with_org`, because none of it carries what
 * any individual said, which is exactly why it is the surface a viewer is
 * allowed to read while a transcript is not.
 *
 * Gaps are the most valuable object in the system and the ordering says so.
 * Farmer.Chat measured that 66% of the questions it could not answer failed
 * because the knowledge base did not cover them, not because the model was
 * weak. A ranked list of what a community is asking that a programme has no
 * answer for is a direct readout of that programme's blind spots, arriving
 * without a survey, and the default sort is by how many people asked.
 *
 * Rumors are the same trick pointed at something more urgent. Internews
 * collected roughly 20,000 rumors across seven countries with human listeners;
 * the same signal arrives here with volume and velocity attached, and forty
 * people in one area asking the same false thing in a week is actionable before
 * it reaches a clinic.
 *
 * This module reads the wire and hands back the console's own types, rather
 * than declaring the two identical and casting. That translation is small and
 * it earns its place twice over.
 *
 * The server ranks **clusters**, not rows: one question across every community
 * that asked it, which is the entire reason the list is worth reading. The
 * screens that carry a question to the knowledge editor need a row, because a
 * document belongs to one project. Both live on `KnowledgeGap` here, and the
 * alternative was to make three screens each pick one and get it wrong.
 *
 * No reader below throws while a row is being drawn. A 404 is not the only
 * thing a real server does: a 200 whose `askCount` arrived as a string or whose
 * `communities` is null crashes a screen exactly as a 404 does, and is harder to
 * see because the status line looks fine. A missing field becomes an empty one
 * here, once, at the boundary, so that no screen has to be written defensively
 * and none of them can be the place it was forgotten.
 *
 * The one deliberate exception is the `items` array of a list route, and the
 * reason is that not throwing and claiming emptiness are different things which
 * this file used to conflate. Coalescing a missing `items` to `[]` does keep the
 * screen standing, and what it then puts on screen is "No unanswered question
 * has been recorded" for a response the console could not read at all. On this
 * product that sentence is the finding, and inventing it is worse than saying
 * the screen could not load: somebody reads it as a fact about their community
 * and stops looking. See `pageItems` below.
 */

export interface GapListQuery extends Query {
  projectId?: string
  /** Absent means open, which is the work. `all` includes the settled ones. */
  status?: GapStatus | 'all'
  /** Questions that arrived in this language among others, not only in it. */
  locale?: Locale
  /**
   * Whether anything has gone back to the people who asked.
   *
   * Not the same question as `status`, and the difference is the platform's:
   * `status=answered` means a document exists, and `loopState=open` means
   * nobody has been told about it.
   */
  loopState?: LoopState
  /** Defaults to `asks`, which is the whole point of the screen. */
  sort?: 'asks' | 'recent' | 'first_seen'
  limit?: number
  cursor?: string
}

export interface TopicListQuery extends Query {
  projectId?: string
  from?: Instant
  to?: Instant
  limit?: number
  cursor?: string
}

export interface RumorListQuery extends Query {
  projectId?: string
  verdict?: RumorVerdict
  from?: Instant
  to?: Instant
  limit?: number
  cursor?: string
}

export interface LoopQuery extends Query {
  projectId?: string
  from?: Instant
  to?: Instant
}

/**
 * The most rows the topic screen will walk before it stops and says so.
 *
 * Twelve weeks of a busy organization is a few hundred rows and the chart needs
 * all of them, so this walks the cursor rather than reading one page; the
 * ceiling is what stops a filter that accidentally matches everything from
 * turning a chart into a tab nobody can close.
 */
const TOPIC_CEILING = 600

export function insightsApi(http: ApiClient) {
  return {
    gaps: {
      async list(query: GapListQuery = {}): Promise<Page<KnowledgeGap>> {
        const page = await http.list<unknown>('/v1/org/insights/gaps', query)
        return {
          items: pageItems(page?.items).map(toGap),
          ...(typeof page?.nextCursor === 'string' ? { nextCursor: page.nextCursor } : {}),
        }
      },

      /**
       * Marks a gap as dismissed, with the reason.
       *
       * A dismissal is a finding in its own right and is kept rather than
       * removing the row: a gap dismissed four times by four different people
       * is telling you something about the programme, not about the gap. The
       * reason is kept in the audit event rather than on the row, because there
       * is no column for it, which means it is readable by `audit:read` and not
       * on this screen. That is a real limitation and it is the honest one.
       */
      async dismiss(gapId: string, reason: string): Promise<void> {
        await http.request<unknown>(path`/v1/org/insights/gaps/${gapId}/dismiss`, {
          method: 'POST',
          body: { reason },
        })
      },

      /**
       * Records which document answered a gap.
       *
       * This is half of closing the loop. The other half is outbound and is
       * owned by broadcasts: the loop only reaches `closed` when something
       * actually went back to the people who asked.
       */
      async resolve(gapId: string, documentId: string): Promise<void> {
        await http.request<unknown>(path`/v1/org/insights/gaps/${gapId}/resolve`, {
          method: 'POST',
          body: { documentId },
        })
      },
    },

    topics: {
      /**
       * Every topic in the window, not the first page of them.
       *
       * The chart is week against week and a page boundary in the middle of a
       * window draws a fortnight that looks like a collapse. `complete` says
       * whether the walk reached the end, so the screen can say the chart is
       * partial instead of quietly drawing a wrong one.
       */
      async list(query: TopicListQuery = {}): Promise<{ items: TopicSignal[]; complete: boolean }> {
        const collected = await http.collect<unknown>(
          '/v1/org/insights/topics',
          {
            ...query,
            // The server's window is named `since`/`until`; every screen here
            // says `from`/`to`, and the translation lives in one place rather
            // than in each of them.
            since: query.from,
            until: query.to,
            from: undefined,
            to: undefined,
          },
          { max: TOPIC_CEILING },
        )
        return { items: collected.items.map(toTopic), complete: collected.complete }
      },
    },

    rumors: {
      async list(query: RumorListQuery = {}): Promise<Page<RumorSignal>> {
        const page = await http.list<unknown>('/v1/org/insights/rumors', {
          ...query,
          since: query.from,
          until: query.to,
          from: undefined,
          to: undefined,
        })
        return {
          items: pageItems(page?.items).map(toRumor),
          ...(typeof page?.nextCursor === 'string' ? { nextCursor: page.nextCursor } : {}),
        }
      },

      /**
       * A human's verdict on a claim. Never the model's.
       *
       * Nothing in this platform decides whether something a community believes
       * is true. Staff do, their name goes on it, and the timestamp goes with
       * it so a verdict that has aged can be seen to have aged.
       */
      async review(id: string, verdict: RumorVerdict, note?: string): Promise<RumorSignal> {
        const body = await http.request<unknown>(path`/v1/org/insights/rumors/${id}/review`, {
          method: 'POST',
          body: { verdict, ...(note === undefined ? {} : { note }) },
        })
        return toRumor(body)
      },
    },

    /**
     * The headline the console reports: how much of what was raised got an
     * answer back.
     *
     * Feedback is defined as working only when it is collected, acknowledged,
     * analysed and responded to. Every other platform can record the inbound
     * half and has to take the outbound half on trust. Namu owns the outbound
     * voice channel, so this is a number a machine can check rather than a
     * policy somebody promises.
     */
    async loops(query: LoopQuery = {}): Promise<LoopSummary> {
      const body = await http.request<unknown>('/v1/org/insights/loops', {
        query: {
          ...query,
          since: query.from,
          until: query.to,
          from: undefined,
          to: undefined,
        },
      })
      return toLoopSummary(body)
    },
  }
}

export interface LoopSummary {
  open: number
  acknowledged: number
  answered: number
  closed: number
  /** Open for longer than the threshold. Surfaced, never aged out. */
  overdue: number
  /** Of everything raised, 0 to 100 rather than a ratio. Computed by the server. */
  closedPct: number
}

export type InsightsApi = ReturnType<typeof insightsApi>

/**
 * What stands in for a question or a claim the server would not send.
 *
 * A sentence rather than an empty cell or a row dropped from the list. The
 * count, the community and the age are the parts that argue for attention and
 * they are all still true; withholding the wording is a deliberate protection
 * and it should read as one, not as something that failed to load.
 */
export const WITHHELD_LABEL = 'Withheld until more people have asked'
export const WITHHELD_CLAIM = 'Withheld until more people are repeating it'

/* ===================== Reading what actually arrived ===================== */

/*
 * Nothing below throws, and the reason is the one rule this console is judged
 * on: a screen must never crash on a bad response. These readers are the single
 * place a missing field, a null where an array was promised or a number that
 * arrived as a string becomes something a table can render. Doing it per screen
 * would mean doing it four times and forgetting it once, and the once is the
 * screen that shows a person "This screen stopped working".
 */

function record(value: unknown): Record<string, unknown> {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {}
}

function readList(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/**
 * The same, for the `items` of a list route, where empty is a claim.
 *
 * The distinction against `readList` is the one this file got wrong, and it is
 * not a distinction about safety: both of these refuse to throw while a row is
 * being drawn, which is the rule the block above states and which stands. It is
 * about what the screen is then entitled to say.
 *
 * A missing `communities` on a gap costs a column. A missing `items` on the
 * whole response costs the finding: Insight renders "No unanswered question has
 * been recorded", which a coordinator reads as a fact about their community and
 * stops looking. On this product that is the more expensive of the two failures
 * by a long way, because the screen exists to say what nobody has answered yet,
 * and the one answer it must never invent is "nothing".
 *
 * `api/cases.ts` reached this conclusion first and wrote it down; `api/tasks.ts`
 * follows it. This is the same rule, in the file that did not.
 */
function pageItems(value: unknown): unknown[] {
  if (!Array.isArray(value)) throw unreadable()
  return value
}

/**
 * A 200 this console cannot read, shaped as the failure it is.
 *
 * `retry` rather than `fatal`: the realistic cause is a proxy that rewrote a
 * body or a server half-way through a deploy, not a request that can never
 * work. It lands in the `catch` every one of these call sites already has, and
 * the screen shows its failure state rather than its empty one.
 */
function unreadable(): ApiError {
  return new ApiError({
    status: 200,
    code: 'internal',
    message: 'Namu sent an answer this console could not read.',
    recovery: 'retry',
  })
}

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback
}

/**
 * A count, or zero.
 *
 * `Number` rather than a type check, because a `bigint` column serialised
 * without a cast arrives as the string `"41"`, and a table that sorts `"9"`
 * after `"10"` puts the most-asked question halfway down the page.
 */
function count(value: unknown): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function optionalCount(value: unknown): number | null {
  if (value === null || value === undefined) return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function locales(value: unknown): Locale[] {
  return readList(value).filter((entry): entry is string => typeof entry === 'string')
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

function optionalOneOf<T extends string>(value: unknown, allowed: readonly T[]): T | null {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : null
}

function day(value: unknown): CalendarDate {
  return text(value)
}

function optionalText(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

const GAP_STATUSES = ['open', 'answered', 'dismissed'] as const
const LOOP_STATES = ['open', 'acknowledged', 'answered', 'closed'] as const
const VERDICTS = ['unreviewed', 'true', 'false', 'misleading'] as const
const DIRECTIONS = ['up', 'down', 'flat', 'new'] as const

function toGapCommunity(raw: unknown): GapCommunity {
  const row = record(raw)
  return {
    gapId: text(row.gapId),
    projectId: text(row.projectId),
    projectName: text(row.projectName),
    locales: locales(row.locales),
    askCount: count(row.askCount),
    memberCount: count(row.memberCount),
    status: oneOf(row.status, GAP_STATUSES, 'open'),
    loopState: optionalOneOf(row.loopState, LOOP_STATES),
    resolvedByDocumentId: optionalText(row.resolvedDocumentId),
  }
}

function toGap(raw: unknown): KnowledgeGap {
  const row = record(raw)
  const communities = readList(row.communities).map(toGapCommunity)
  /*
   * The server orders the communities by how many people asked, so the first is
   * the one whose wording the most people are behind and the one an action
   * should default to. `??` rather than an assumption that there is one: a
   * cluster whose rows were filtered away between the server's two queries is a
   * race, and it should produce a row with no actions rather than a crash.
   */
  const representative = communities[0]
  const label = optionalText(row.label)

  return {
    id: representative?.gapId ?? text(row.clusterKey),
    projectId: representative?.projectId ?? '',
    clusterKey: text(row.clusterKey),
    question: label ?? WITHHELD_LABEL,
    labelWithheld: label === null,
    locales: locales(row.locales),
    askCount: count(row.askCount),
    memberCount: count(row.memberCount),
    firstSeenAt: day(row.firstAskedAt),
    lastSeenAt: day(row.lastAskedAt),
    status: oneOf(row.status, GAP_STATUSES, 'open'),
    loopState: optionalOneOf(row.loopState, LOOP_STATES),
    resolvedByDocumentId: representative?.resolvedByDocumentId ?? null,
    communities,
  }
}

function toTopic(raw: unknown): TopicSignal {
  const row = record(raw)
  return {
    id: text(row.id),
    topic: text(row.topic),
    projectId: text(row.projectId),
    periodStart: text(row.periodStart),
    periodEnd: text(row.periodEnd),
    askCount: count(row.askCount),
    memberCount: count(row.memberCount),
    previousCount: optionalCount(row.previousCount),
    change: count(row.change),
    changeRatio: optionalCount(row.changeRatio),
    direction: oneOf(row.direction, DIRECTIONS, 'flat'),
  }
}

function toRumor(raw: unknown): RumorSignal {
  const row = record(raw)
  const claim = optionalText(row.claim)
  return {
    id: text(row.id),
    projectId: text(row.projectId),
    claim: claim ?? WITHHELD_CLAIM,
    claimWithheld: claim === null,
    locale: text(row.locale),
    heardCount: count(row.heardCount),
    memberCount: count(row.memberCount),
    firstHeardAt: day(row.firstHeardAt),
    lastHeardAt: day(row.lastHeardAt),
    verdict: oneOf(row.verdict, VERDICTS, 'unreviewed'),
    reviewedAt: optionalText(row.reviewedAt),
    knowledgeDocumentId: optionalText(row.knowledgeDocumentId),
  }
}

function toLoopSummary(raw: unknown): LoopSummary {
  const row = record(raw)
  return {
    open: count(row.open),
    acknowledged: count(row.acknowledged),
    answered: count(row.answered),
    closed: count(row.closed),
    overdue: count(row.overdue),
    closedPct: count(row.closedPct),
  }
}
